import React from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Card, CardContent, CardHeader, Divider, Typography, Box } from '@mui/material';
import type { CardProps, CardHeaderProps, CardContentProps, BoxProps } from '@mui/material';

// constant
const headerSX = {
    '& .MuiCardHeader-action': { mr: 0 }
};
const footerSx = {
    display: 'flex',
    justifyContent: 'flex-end',
    p: 2
};

// ==============================|| CUSTOM MAIN CARD ||============================== //

export interface MainCardProps extends Omit<CardProps, 'title' | 'children' | 'sx' | 'content'> {
    border?: boolean;
    boxShadow?: boolean;
    children?: React.ReactNode;
    content?: boolean;
    contentClass?: string;
    contentSX?: CardContentProps['sx'];
    footerSX?: BoxProps['sx'];
    darkTitle?: boolean;
    sx?: CardProps['sx'];
    secondary?: CardHeaderProps['action'];
    shadow?: string | number;
    elevation?: number;
    title?: React.ReactNode;
    footer?: React.ReactNode;
}

const MainCard = React.forwardRef<HTMLDivElement, MainCardProps>(
    (
        {
            boxShadow,
            children,
            title,
            darkTitle,
            secondary,
            footer,
            shadow,
            border = true,
            content = true,
            contentClass = '',
            contentSX = {},
            footerSX = {},
            sx = {},
            ...others
        },
        ref
    ) => {
        const theme = useTheme();

        return (
            <Card
                ref={ref}
                {...others}
                sx={{
                    border: border ? '1px solid' : 'none',
                    borderColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.primary[200] + 75,
                    ':hover': {
                        boxShadow: boxShadow
                            ? shadow ||
                              (theme.palette.mode === 'dark' ? '0 2px 14px 0 rgb(33 150 243 / 10%)' : '0 2px 14px 0 rgb(32 40 45 / 8%)')
                            : 'inherit'
                    },
                    ...sx
                }}
            >
                {/* card header and action */}
                {!darkTitle && title && <CardHeader sx={headerSX} title={title} action={secondary} />}
                {darkTitle && title && (
                    <CardHeader sx={headerSX} title={<Typography variant="h3">{title}</Typography>} action={secondary} />
                )}

                {/* content & header divider */}
                {title && <Divider />}

                {/* card content */}
                {content && (
                    <CardContent sx={contentSX} className={contentClass}>
                        {children}
                    </CardContent>
                )}
                {!content && children}
                {footer && <Box sx={{ ...footerSx, ...footerSX }}>{footer}</Box>}
            </Card>
        );
    }
);

export default MainCard;
